import { apiBase } from '../appConfig.ts'
import { get, post, put } from './client.ts'
import type { LoginResponse, User } from '../types/auth.ts'

export async function login(username: string, password: string): Promise<LoginResponse> {
  // FastAPI OAuth2 expects form-encoded data, not JSON
  const response = await fetch(`${apiBase()}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ username, password }),
  })

  if (!response.ok) {
    const body = await response.json().catch(() => ({ detail: 'Login failed' }))
    throw new Error(body.detail || 'Login failed')
  }

  return response.json()
}

export function getCurrentUser(): Promise<User> {
  return get<User>('/auth/me')
}

export function registerUser(data: {
  username: string
  password: string
  full_name?: string
  email?: string
}): Promise<User> {
  return post<User>('/auth/register', data)
}

/** Check if setup is needed (no users exist). Returns true if setup is needed. */
export async function checkSetupNeeded(): Promise<boolean> {
  try {
    const response = await fetch(`${apiBase()}/auth/setup`)
    if (!response.ok) return false
    const data = await response.json()
    return data.setup_needed === true
  } catch {
    return false
  }
}

/** Create the first admin user. */
export async function setupFirstAdmin(data: {
  username: string
  password: string
  full_name?: string
  email?: string
}): Promise<User> {
  return post<User>('/auth/setup', data)
}

/** Change the current user's password. Throws ApiError on wrong current password. */
export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return post<void>('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  })
}

/** Admin: list all users. */
export function listUsers(): Promise<User[]> {
  return get<User[]>('/auth/users')
}

/** Admin: update another user's active status and/or reset their password. */
export function adminUpdateUser(
  userId: number,
  data: { is_active?: boolean; new_password?: string },
): Promise<User> {
  return post<User>(`/auth/users/${userId}`, data)
}

/** Admin: set a user's roles/capabilities to exactly `capabilities`.
 *  Anything in the known vocabulary that is not listed is revoked; every
 *  change is audited server-side. */
export function setUserCapabilities(userId: number, capabilities: string[]): Promise<User> {
  return put<User>(`/auth/users/${userId}/capabilities`, { capabilities })
}
